<template>
  <data-table min-width="1410px" class="ai-visibility-citation-sources-table">
    <template #head>
      <data-table-row header fill>
        <data-table-cell
            v-for="column in columns"
            :key="column.key"
            :width="column.width"
            :align="column.align || 'left'"
            muted
            header
        >
          <button
              v-if="column.sortable"
              type="button"
              class="ai-visibility-citation-sources-table__sort"
              @click="handleSort(column.key)"
          >
            <span>{{ column.label }}</span>
            <i class="material-icons">{{ sortIcon(column.key) }}</i>
          </button>
          <span v-else>{{ column.label }}</span>
        </data-table-cell>
      </data-table-row>
    </template>

    <template #body>
      <template v-if="rows.length">
        <template v-if="groupByWebsite">
          <template v-for="row in rows">
            <data-table-row
                :key="row.id"
                class="ai-visibility-citation-sources-table__group-row"
            >
              <data-table-cell width="430px" class="right-border">
                <button
                    type="button"
                    class="ai-visibility-citation-sources-table__disclosure"
                    :aria-expanded="isExpanded(row.id) ? 'true' : 'false'"
                    @click="toggleWebsite(row.id)"
                >
                  <source-summary
                      :title="row.website"
                      :domain="row.website"
                      class="title-large ai-visibility-citation-sources-table__group-source"
                  >
                    <i class="material-icons">{{ isExpanded(row.id) ? 'expand_less' : 'expand_more' }}</i>
                  </source-summary>
                </button>
              </data-table-cell>
              <data-table-cell width="200px">
                <span class="ai-visibility-citation-sources-table__pages">{{ row.pagesCount }}</span>
              </data-table-cell>
              <data-table-cell width="220px">
                <score-bar
                    :value="row.citations.value"
                    :max="row.citations.max"
                />
              </data-table-cell>
              <data-table-cell width="220px" align="center">
                <status-pill
                    :tone="row.mentionStatus.tone"
                    :label="row.mentionStatus.label"
                />
              </data-table-cell>
              <data-table-cell width="180px">
                <citation-opportunity-cell :opportunity="row.opportunity"/>
              </data-table-cell>
              <data-table-cell width="160px" align="right">
                <span class="ai-visibility-citation-sources-table__date">{{ formatLastSeen(row.lastSeen) }}</span>
              </data-table-cell>
            </data-table-row>
            <template v-if="isExpanded(row.id)">
              <data-table-row
                  v-for="child in row.children"
                  :key="`${row.id}-${child.id}`"
                  class="ai-visibility-citation-sources-table__child-row"
              >
                <data-table-cell width="430px">
                  <div class="ai-visibility-citation-sources-table__child-page">
                    <citation-source-page-cell
                        :page="child.citedPage"
                        :website="child.website"
                        :show-avatar="false"
                        clickable
                        @select="$emit('select-citation', child)"
                    />
                  </div>
                </data-table-cell>
                <data-table-cell width="200px">
                  <span class="ai-visibility-citation-sources-table__child-spacer" aria-hidden="true"></span>
                </data-table-cell>
                <data-table-cell width="220px">
                  <score-bar
                      :value="child.citations.value"
                      :max="child.citations.max"
                  />
                </data-table-cell>
                <data-table-cell width="220px" align="center">
                  <status-pill
                      :tone="child.mentionStatus.tone"
                      :label="child.mentionStatus.label"
                  />
                </data-table-cell>
                <data-table-cell width="180px">
                  <citation-opportunity-cell :opportunity="child.opportunity"/>
                </data-table-cell>
                <data-table-cell width="160px" align="right">
                  <span class="ai-visibility-citation-sources-table__date">{{ formatLastSeen(child.lastSeen) }}</span>
                </data-table-cell>
              </data-table-row>
            </template>
          </template>
        </template>
        <template v-else>
          <data-table-row
              v-for="row in rows"
              :key="row.id"
          >
            <data-table-cell width="430px" class="right-border">
              <citation-source-page-cell
                  :page="row.citedPage"
                  :website="row.website"
                  clickable
                  @select="$emit('select-citation', row)"
              />
            </data-table-cell>
            <data-table-cell width="200px">
              <span class="ai-visibility-citation-sources-table__website">{{ row.website }}</span>
            </data-table-cell>
            <data-table-cell width="220px">
              <score-bar
                  :value="row.citations.value"
                  :max="row.citations.max"
              />
            </data-table-cell>
            <data-table-cell width="220px" align="center">
              <status-pill
                  :tone="row.mentionStatus.tone"
                  :label="row.mentionStatus.label"
              />
            </data-table-cell>
            <data-table-cell width="180px">
              <citation-opportunity-cell :opportunity="row.opportunity"/>
            </data-table-cell>
            <data-table-cell width="160px" align="right">
              <span class="ai-visibility-citation-sources-table__date">{{ formatLastSeen(row.lastSeen) }}</span>
            </data-table-cell>
          </data-table-row>
        </template>
      </template>
      <div v-else class="ai-visibility-citation-sources-table__empty">
        <empty-state
            title="No sources found"
            description="Try a different search term."
        />
      </div>
    </template>
  </data-table>
</template>

<script>
import DataTable from '../../../ui/table/DataTable.vue'
import DataTableRow from '../../../ui/table/DataTableRow.vue'
import DataTableCell from '../../../ui/table/DataTableCell.vue'
import ScoreBar from '../../../ui/display/ScoreBar.vue'
import StatusPill from '../../../ui/display/StatusPill.vue'
import SourceSummary from '../../../ui/display/SourceSummary.vue'
import EmptyState from '../../../ui/states/EmptyState.vue'
import CitationSourcePageCell from './CitationSourcePageCell.vue'
import CitationOpportunityCell from './CitationOpportunityCell.vue'

const COLUMNS = [
  {key: 'citedPage', label: 'Page AI cited', width: '430px'},
  {key: 'website', label: 'Website', width: '200px'},
  {key: 'citations', label: 'Citations', width: '220px', sortable: true},
  {key: 'mentioned', label: 'Source mentions you?', width: '220px', align: 'center', sortable: true},
  {key: 'opportunity', label: 'Opportunity', width: '180px', sortable: true},
  {key: 'lastSeen', label: 'Last seen', width: '160px', align: 'right'},
]
const GROUPED_COLUMNS = [
  {key: 'website', label: 'Website', width: '430px'},
  {key: 'pages', label: 'Pages', width: '200px'},
  {key: 'citations', label: 'Citations', width: '220px', sortable: true},
  {key: 'mentioned', label: 'Source mentions you?', width: '220px', align: 'center', sortable: true},
  {key: 'opportunity', label: 'Opportunity', width: '180px', sortable: true},
  {key: 'lastSeen', label: 'Last seen', width: '160px', align: 'right'},
]

export default {
  name: 'CitationSourcesTable',
  components: {
    DataTable,
    DataTableRow,
    DataTableCell,
    ScoreBar,
    StatusPill,
    SourceSummary,
    EmptyState,
    CitationSourcePageCell,
    CitationOpportunityCell,
  },
  props: {
    rows: {
      type: Array,
      required: true,
    },
    sortKey: {
      type: String,
      required: true,
    },
    sortDirection: {
      type: String,
      required: true,
    },
    groupByWebsite: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedWebsiteIds: {},
    }
  },
  computed: {
    columns() {
      return this.groupByWebsite ? GROUPED_COLUMNS : COLUMNS
    },
  },
  watch: {
    rows() {
      this.expandedWebsiteIds = {}
    },
    groupByWebsite() {
      this.expandedWebsiteIds = {}
    },
  },
  methods: {
    isExpanded(rowId) {
      return Boolean(this.expandedWebsiteIds[rowId])
    },
    toggleWebsite(rowId) {
      if (this.isExpanded(rowId)) {
        this.$delete(this.expandedWebsiteIds, rowId)
        return
      }
      this.$set(this.expandedWebsiteIds, rowId, true)
    },
    handleSort(key) {
      const direction = this.sortKey === key && this.sortDirection === 'desc' ? 'asc' : 'desc'
      this.$emit('sort-change', {key, direction})
    },
    sortIcon(key) {
      if (this.sortKey !== key) {
        return 'unfold_more'
      }
      return this.sortDirection === 'desc' ? 'expand_more' : 'expand_less'
    },
    formatLastSeen(value) {
      if (!value) {
        return ''
      }
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return String(value)
      }
      return new Intl.DateTimeFormat('en-GB', {
        month: 'numeric',
        day: 'numeric'
      }).format(parsed).replace(/\//g, '.');
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-citation-sources-table {
  border-right: 0;
  border-left: 0;
  border-radius: 0;

  ::v-deep .ai-visibility-data-table-row:last-child::after {
    border-bottom: none;
  }
}

.ai-visibility-citation-sources-table__sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  padding: 0;
}

.ai-visibility-citation-sources-table__sort:focus {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}

.ai-visibility-citation-sources-table__sort i {
  font-size: 18px;
}

.ai-visibility-citation-sources-table__website {
  font-size: 16px;
  font-weight: 400;
  color: var(--v-blackColor-base);
}

.ai-visibility-citation-sources-table__group-row {
  background: var(--v-cardBg-base);
}

.ai-visibility-citation-sources-table__disclosure {
  display: inline-flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  border: 0;
  background: transparent;
  color: var(--v-blackColor-base);
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
}

.ai-visibility-citation-sources-table__disclosure:focus {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}

.ai-visibility-citation-sources-table__disclosure i {
  flex: 0 0 auto;
  color: var(--v-link3Color-base);
  font-size: 20px;
}

.ai-visibility-citation-sources-table__group-source {
  width: 100%;
}

.ai-visibility-citation-sources-table__pages {
  color: var(--v-blackColor-base);
  font-size: 16px;
  font-weight: 600;
}

.ai-visibility-citation-sources-table__child-row {
  ::v-deep .ai-visibility-data-table-cell {
    min-height: 48px;
    align-content: center;
  }

  ::v-deep .ai-visibility-score-bar__track {
    background-color: #DADADA;

    .ai-visibility-score-bar__fill {
      background-color: var(--v-darkGrayColor-base);
    }
  }

}

.ai-visibility-citation-sources-table__child-page {
  padding-left: 48px;
}

.ai-visibility-citation-sources-table__child-spacer {
  display: block;
}

.ai-visibility-citation-sources-table__date {
  color: var(--v-blackColor-base);
  font-weight: 400;
}

.ai-visibility-citation-sources-table__empty {
  padding: 18px;
}
</style>
